import React, { useState, useEffect, useRef } from "react";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Bell, CalendarIcon, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DateTime } from "luxon";
import ReactCalendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Switch } from "../../../ui/switch";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { createCheckpointReminder } from "../../../../api/checkpointReminders";

interface CheckpointReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  activityTitle?: string;
  processId: string;
  notes?: any[];
}

type ReminderOption = {
  label: string;
  value: number; // days from now
  description: string;
};

const CheckpointReminderModal = ({
  isOpen,
  onClose,
  activityTitle = "this activity",
  processId,
  notes = [],
}: CheckpointReminderModalProps) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingReminders, setExistingReminders] = useState<any[]>([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [prescriptionHasReminders, setPrescriptionHasReminders] = useState(false);
  const [reminderSettings, setReminderSettings] = useState<any>(null);

  // Custom date and time state
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [showCalendar, setShowCalendar] = useState(false);

  const calendarRef = useRef<HTMLDivElement>(null);

  // Fetch existing reminders - extracted as separate function for reusability
  const fetchExistingReminders = async () => {
    if (!processId) return;

    setLoadingReminders(true);
    try {
      const { getProcessReminders } = await import("../../../../api/checkpointReminders");
      const response = await getProcessReminders(retryFetch, processId);
      // Filter for pending author_default reminders
      const authorReminders = response.reminders.filter(
        (r: any) => r.reminderType === "author_default" && r.status === "pending"
      );
      setExistingReminders(authorReminders);

      // Check if prescription has reminder settings (we can infer from any reminder)
      if (response.reminders.length > 0 && response.reminders[0].reminderType === "author_default") {
        setPrescriptionHasReminders(true);
        // Store reminder settings for re-enabling
        const firstReminder = response.reminders[0];
        setReminderSettings({
          frequency: firstReminder.frequency,
          time: firstReminder.time,
        });
      }
    } catch (error) {
      console.error("Failed to fetch reminders:", error);
      toast.error("Failed to load existing reminders");
    } finally {
      setLoadingReminders(false);
    }
  };

  // Fetch existing reminders when modal opens
  useEffect(() => {
    if (isOpen && processId) {
      fetchExistingReminders();
    }
  }, [isOpen, processId]);

  // Initialize with default time (9:00 AM)
  useEffect(() => {
    if (isOpen && !selectedTime) {
      setSelectedTime("09:00");
    }
  }, [isOpen]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  const reminderOptions: ReminderOption[] = [
    {
      label: t("reminder_option_tomorrow"),
      value: 1,
      description: t("reminder_option_tomorrow_desc"),
    },
    {
      label: t("reminder_option_2days"),
      value: 2,
      description: t("reminder_option_2days_desc"),
    },
    {
      label: t("reminder_option_1week"),
      value: 7,
      description: t("reminder_option_1week_desc"),
    },
  ];

  // Handle quick option selection
  const handleQuickOptionSelect = (days: number) => {
    setSelectedOption(days);
    // Calculate date based on selected option
    const futureDate = DateTime.now().plus({ days }).toJSDate();
    setSelectedDate(futureDate);
  };

  // Handle custom date change
  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    setShowCalendar(false);
    // Clear quick option selection when custom date is picked
    setSelectedOption(null);
  };

  // Handle custom time change
  const handleTimeChange = (time: string) => {
    setSelectedTime(time);
  };

  // Get the final scheduled DateTime
  const getScheduledDateTime = (): DateTime => {
    const [hours, minutes] = selectedTime.split(":").map(Number);
    return DateTime.fromJSDate(selectedDate).set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
  };

  // Format date for display
  const formatDateDisplay = (): string => {
    const dt = DateTime.fromJSDate(selectedDate);
    const today = DateTime.now().startOf('day');
    const tomorrow = today.plus({ days: 1 });

    if (dt.hasSame(today, 'day')) {
      return t('reminder_today');
    } else if (dt.hasSame(tomorrow, 'day')) {
      return t('reminder_tomorrow');
    } else {
      return dt.toFormat('MMM dd, yyyy');
    }
  };

  const handleSetReminder = async () => {
    const scheduledDateTime = getScheduledDateTime();

    // Validate that the scheduled time is in the future
    if (scheduledDateTime <= DateTime.now()) {
      toast.warning(t("reminder_past_time_warning"));
      return;
    }

    setIsSubmitting(true);

    try {
      // Separate action items and insights
      const actionItems = notes
        .filter((note) => note.type === "action" && note.content && note.content.trim() !== "")
        .map((note) => note.content);

      const insights = notes
        .filter((note) => note.type === "insight" && note.content && note.content.trim() !== "")
        .map((note) => note.content);

      // Create the reminder via API
      await createCheckpointReminder(retryFetch, {
        processId,
        scheduledDateTime: scheduledDateTime.toISO() || scheduledDateTime.toString(),
        activityTitle,
        actionItems: actionItems,
        insights: insights,
      });

      const timeFromNow = scheduledDateTime.toRelative();
      toast.success(
        t("reminder_set_success", { timeFromNow })
      );
      handleClose();
    } catch (error) {
      console.error("Failed to set reminder:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to set reminder. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleReminder = async (reminderId: string, currentlyActive: boolean) => {
    if (currentlyActive) {
      // Toggle OFF - cancel the reminder
      try {
        const { cancelReminder } = await import("../../../../api/checkpointReminders");
        await cancelReminder(retryFetch, reminderId);
        toast.success("Automatic reminder disabled");

        // Refetch reminders list from server to ensure sync
        await fetchExistingReminders();
      } catch (error) {
        console.error("Failed to disable reminder:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to disable reminder";

        // Handle "already cancelled" gracefully
        if (errorMessage.includes("already cancelled")) {
          toast.info("Reminder was already disabled");
          await fetchExistingReminders(); // Refresh to sync UI
        } else {
          toast.error(errorMessage);
        }
      }
    } else {
      // Toggle ON - enable/create the reminder
      try {
        const { enableDefaultReminder } = await import("../../../../api/checkpointReminders");
        await enableDefaultReminder(retryFetch, processId, activityTitle);
        toast.success("Automatic reminder enabled");

        // Refetch reminders list from server to ensure sync
        await fetchExistingReminders();
      } catch (error) {
        console.error("Failed to enable reminder:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to enable reminder";
        toast.error(errorMessage);
      }
    }
  };

  const handleClose = () => {
    setSelectedOption(null);
    setSelectedDate(new Date());
    setSelectedTime("");
    setShowCalendar(false);
    setExistingReminders([]);
    setPrescriptionHasReminders(false);
    setReminderSettings(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
              <Bell className="w-6 h-6 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-center">
            {t("checkpoint_reminder_modal_title")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t("checkpoint_reminder_modal_description")}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Automatic Reminder Settings - Show toggle even when disabled */}
          {(existingReminders.length > 0 || prescriptionHasReminders) && (
            <div className="p-4 space-y-3 border rounded-lg bg-primary/5 dark:bg-indigo-950/20 border-primary/20 dark:border-indigo-900">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-primary" />
                <h4 className="font-medium text-sm text-primary dark:text-primary-foreground/80">
                  Automatic Reminder Settings
                </h4>
              </div>

              {existingReminders.length > 0 ? (
                // Active reminder - show with toggle ON
                existingReminders.map((reminder) => (
                <div key={reminder._id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">
                        {reminder.frequency.charAt(0).toUpperCase() + reminder.frequency.slice(1)} at {reminder.time}
                      </p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        Active
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Next: {DateTime.fromISO(reminder.scheduledDateTime).toLocaleString(DateTime.DATETIME_MED)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-muted-foreground mb-1">Enable</span>
                      <Switch
                        checked={true}
                        onCheckedChange={() => handleToggleReminder(reminder._id, true)}
                      />
                    </div>
                  </div>
                </div>
                ))
              ) : (
                // No active reminder but prescription has settings - show toggle OFF
                <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">
                        {reminderSettings?.frequency && reminderSettings.frequency.charAt(0).toUpperCase() + reminderSettings.frequency.slice(1)} reminder at {reminderSettings?.time || "9:00 AM"}
                      </p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                        Disabled
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Reminder is currently turned off
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-muted-foreground mb-1">Enable</span>
                      <Switch
                        checked={false}
                        onCheckedChange={() => handleToggleReminder("", false)}
                      />
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                This automatic reminder was configured by the workflow author. You can {existingReminders.length > 0 ? "disable" : "enable"} it using the toggle above.
              </p>
            </div>
          )}

          {/* Quick Time Selection Buttons */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Clock className="w-4 h-4" />
              {t("reminder_when_prompt")}
            </label>
            <div className="grid gap-2">
              {reminderOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleQuickOptionSelect(option.value)}
                  className={`
                    relative flex items-start gap-3 p-3 rounded-lg border-2 transition-all
                    hover:border-primary/50 hover:bg-muted/50
                    ${
                      selectedOption === option.value
                        ? "border-primary bg-primary/5"
                        : "border-border"
                    }
                  `}
                  disabled={isSubmitting}
                >
                  {/* Radio indicator */}
                  <div className="mt-0.5">
                    <div
                      className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center
                      ${
                        selectedOption === option.value
                          ? "border-primary"
                          : "border-muted-foreground/30"
                      }
                    `}
                    >
                      {selectedOption === option.value && (
                        <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2 font-medium">
                      <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                      {option.label}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {option.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-2 bg-background text-muted-foreground">
                {t("reminder_or_customize")}
              </span>
            </div>
          </div>

          {/* Custom Date and Time Picker */}
          <div className="space-y-3">
            {/* Date Picker */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <CalendarIcon className="w-4 h-4" />
                {t("reminder_select_date")}
              </Label>
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="justify-start w-full font-normal text-left"
                  type="button"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {formatDateDisplay()}
                </Button>

                {showCalendar && (
                  <div
                    ref={calendarRef}
                    className="absolute z-50 w-full p-3 mt-2 border rounded-md shadow-lg bg-background"
                  >
                    <ReactCalendar
                      onChange={(value) => handleDateChange(value as Date)}
                      value={selectedDate}
                      minDate={new Date()}
                      className="w-full border-none"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Time Picker */}
            <div className="space-y-2">
              <Label htmlFor="time-picker" className="flex items-center gap-2 text-sm font-medium">
                <Clock className="w-4 h-4" />
                {t("reminder_select_time")}
              </Label>
              <Input
                id="time-picker"
                type="time"
                value={selectedTime}
                onChange={(e) => handleTimeChange(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Preview of scheduled time */}
            {selectedTime && (
              <div className="p-3 text-sm rounded-lg bg-primary/10">
                <p className="font-medium text-primary">
                  {t("reminder_scheduled_for")}
                </p>
                <p className="mt-1 text-muted-foreground">
                  {formatDateDisplay()} at {selectedTime}
                  {" "}({getScheduledDateTime().toRelative()})
                </p>
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="p-3 text-sm rounded-lg bg-muted/50 text-muted-foreground">
            <p className="flex items-start gap-2">
              <Bell className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {t("reminder_info_message")}
              </span>
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1"
          >
            {t("reminder_not_now")}
          </Button>
          <Button
            onClick={handleSetReminder}
            disabled={!selectedTime || isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? t("reminder_setting") : t("reminder_set_button")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CheckpointReminderModal;
